import { runtimeConfig, type RuntimeConfig } from '@/config/env'
import type { AuthGateway } from '@/features/auth'
import { createFixtureAuthGateway, fixtureAccounts } from '@/features/auth/infrastructure/FixtureAuthGateway'
import { createSupabaseAuthGateway } from '@/features/auth/infrastructure/SupabaseAuthGateway'
import { createSnapshotDetectionSource, type DetectionSource } from '@/features/mission/application/detectionSource'
import type { MissionRepository } from '@/features/mission/application/MissionRepository'
import { createFixtureMissionRepository } from '@/features/mission/infrastructure/fixtures/FixtureMissionRepository'
import { createSupabaseMissionRepository } from '@/features/mission/infrastructure/supabase/SupabaseMissionRepository'
import { createSupabaseClient } from '@/infrastructure/supabase/client'
import {
  createBrowserGeolocation,
  createSimulatedGeolocation,
  type GeolocationPort,
  type SimulatedGeolocation,
} from '@/platform/geolocation'
import { createBrowserWakeLock, type WakeLockPort } from '@/platform/wakeLock'

export interface AppDependencies {
  config: RuntimeConfig
  auth: AuthGateway
  missions: MissionRepository
  geolocation: GeolocationPort
  wakeLock: WakeLockPort
  /** Profil de détection et drapeaux figés dans la mission par le serveur (ADR-018). */
  detection: DetectionSource
}

declare global {
  interface Window {
    /** Positions fictives (fixtures + laboratoire seulement) : essais Playwright et démonstrations. */
    __recaGps?: Pick<SimulatedGeolocation, 'emit' | 'release'>
  }
}

function fixtureUserId(): string | null {
  try {
    const email = window.localStorage.getItem('reca-operator-v3-fixture-session')
    return email ? (fixtureAccounts[email]?.session.userId ?? null) : null
  } catch {
    return null
  }
}

export function composeDependencies(config: RuntimeConfig): AppDependencies {
  const platform = { geolocation: createBrowserGeolocation(), wakeLock: createBrowserWakeLock() }
  if (config.dataSource === 'fixtures') {
    let geolocation: GeolocationPort = platform.geolocation
    if (config.labEnabled && typeof window !== 'undefined') {
      const simulated = createSimulatedGeolocation(platform.geolocation)
      window.__recaGps = { emit: simulated.emit, release: simulated.release }
      geolocation = simulated
    }
    return {
      config,
      auth: createFixtureAuthGateway(),
      missions: createFixtureMissionRepository(fixtureUserId),
      ...platform,
      geolocation,
      detection: createSnapshotDetectionSource(true),
    }
  }
  const client = createSupabaseClient(config.supabaseUrl, config.supabaseAnonKey)
  return {
    config,
    auth: createSupabaseAuthGateway(client),
    missions: createSupabaseMissionRepository(client),
    ...platform,
    detection: createSnapshotDetectionSource(false),
  }
}

export const appDependencies = composeDependencies(runtimeConfig)
